import { DEFAULT_FORM_FONT_SIZE, FormFontSize, normalizeFormFontSize } from "./ingestion.form.font-size";

/** Parsed `ingestion_field_definition.field_config` for SECTION fields. */
export interface SectionFieldConfig {
    headerImageKey?: string;
    headingFontSize: FormFontSize;
    headingColor?: string;
}

const DEFAULT_CONFIG: SectionFieldConfig = {
    headingFontSize: DEFAULT_FORM_FONT_SIZE,
};

function normalizeOptionalString(raw: unknown): string | undefined {
    if (raw == null || typeof raw !== "string") {
        return undefined;
    }
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

function parseFieldConfigObject(fieldConfig: string | null | undefined): Record<string, unknown> {
    if (fieldConfig == null || String(fieldConfig).trim() === "") {
        return {};
    }
    try {
        const parsed = JSON.parse(fieldConfig) as unknown;
        return parsed != null && typeof parsed === "object" && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : {};
    } catch {
        return {};
    }
}

/**
 * Parses JSON from `fieldDef.fieldConfig` for section fields.
 * Unrelated keys are preserved for merge on stringify.
 */
export function parseSectionFieldConfig(fieldConfig: string | null | undefined): SectionFieldConfig {
    if (fieldConfig == null || String(fieldConfig).trim() === "") {
        return { ...DEFAULT_CONFIG };
    }
    try {
        const raw = JSON.parse(fieldConfig) as Record<string, unknown>;
        const parsed: SectionFieldConfig = {
            headingFontSize: normalizeFormFontSize(raw.headingFontSize),
        };
        const headerImageKey = normalizeOptionalString(raw.headerImageKey);
        if (headerImageKey) {
            parsed.headerImageKey = headerImageKey;
        }
        const headingColor = normalizeOptionalString(raw.headingColor);
        if (headingColor) {
            parsed.headingColor = headingColor;
        }
        return parsed;
    } catch {
        return { ...DEFAULT_CONFIG };
    }
}

/** Merges section branding keys into existing `field_config` JSON. */
export function stringifySectionFieldConfig(config: SectionFieldConfig, existingFieldConfig?: string | null): string {
    const payload = parseFieldConfigObject(existingFieldConfig);

    if (config.headerImageKey) {
        payload.headerImageKey = config.headerImageKey;
    } else {
        delete payload.headerImageKey;
        delete payload.headerImageAlt;
    }

    if (config.headingFontSize !== DEFAULT_FORM_FONT_SIZE) {
        payload.headingFontSize = config.headingFontSize;
    } else {
        delete payload.headingFontSize;
    }

    if (config.headingColor) {
        payload.headingColor = config.headingColor;
    } else {
        delete payload.headingColor;
    }

    return JSON.stringify(payload);
}
